import React, { useRef, useEffect, useState, useMemo } from 'react';
import { C, th } from './shared';
import TableRow from './TableRow';

// ── Skeleton row ──────────────────────────────────────────────────────────────
function SkeletonCell() {
  return (
    <td style={{ padding: '10px', border: `1px solid #E2E8F0` }}>
      <div className="analyzer-skeleton" style={{
        height: 12, background: '#E2E8F0', borderRadius: 4,
      }} />
    </td>
  );
}

function SkeletonRow({ cols }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => <SkeletonCell key={i} />)}
    </tr>
  );
}

// ── Sort-able header (server-side) ────────────────────────────────────────────
function SortTh({ col, sortCol, sortDir, onSort, children, style = {}, rowSpan, title }) {
  // `title` раніше не приймався взагалі, тож підказки, які вже передавались
  // (Worst Yr%, Profit.Mo%, Calmar…), мовчки нікуди не потрапляли.
  const active = sortCol === col;
  return (
    <th
      rowSpan={rowSpan}
      title={title}
      style={{
        ...th, ...style,
        color: active ? C.primary : C.textMd,
        background: active ? C.primaryLt : (style.background || '#F8FAFC'),
        fontWeight: active ? 700 : 600,
      }}
      onClick={() => onSort(col)}
    >
      {children} {active ? (sortDir > 0 ? '↑' : '↓') : ''}
    </th>
  );
}

// ── Sort-able sub-header (client-side, for period columns) ────────────────────
function PeriodSubTh({ active, dir, onClick, children, style = {} }) {
  return (
    <th
      style={{
        ...th,
        ...style,
        fontSize: 11,
        padding: '4px 6px',
        cursor: 'pointer',
        background: active ? '#DCFCE7' : '#F0FDF4',
        color: active ? '#15803D' : '#6B7280',
        fontWeight: active ? 700 : 500,
        userSelect: 'none',
      }}
      onClick={onClick}
    >
      {children} {active ? (dir > 0 ? '↑' : '↓') : ''}
    </th>
  );
}

function ChainSubTh({ active, dir, onClick, children }) {
  return (
    <th
      style={{
        ...th,
        fontSize: 11,
        padding: '4px 6px',
        cursor: 'pointer',
        background: active ? '#EDE9FE' : '#F5F3FF',
        color: active ? '#5B21B6' : '#6B7280',
        fontWeight: active ? 700 : 500,
        userSelect: 'none',
      }}
      onClick={onClick}
    >
      {children} {active ? (dir > 0 ? '↑' : '↓') : ''}
    </th>
  );
}

// ── Main ResultsTable ─────────────────────────────────────────────────────────
export default function ResultsTable({
  results,
  loading,
  pageOffset,
  sortCol,
  sortDir,
  onSort,
  visibleColumns,
  checkedIds,
  starredIds,
  showFavoritesOnly,
  onToggleCheck,
  onToggleStar,
  onSelectAll,
  onViewDetail,
  onCopyToForm,
  onRunMock,
  onRecalcOne,
  onDelete,
  recalcingId,
  onToggleFavorites,
  onOpenPeriods,
  runningBotNames = [],
}) {
  const topScrollRef   = useRef(null);
  const tableScrollRef = useRef(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);

  // Client-side sort state for period sub-columns
  // { idx: number, metric: 'monthly'|'yearly', dir: 1|-1 }
  const [periodSort, setPeriodSort] = useState(null);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setTableScrollWidth(el.scrollWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Re-measure scroll width whenever results or visible columns change (table may grow wider)
  useEffect(() => {
    if (tableScrollRef.current) {
      setTableScrollWidth(tableScrollRef.current.scrollWidth);
    }
  }, [results, visibleColumns]);

  function syncFromTop(e)    { if (tableScrollRef.current) tableScrollRef.current.scrollLeft = e.target.scrollLeft; }
  function syncFromBottom(e) { if (topScrollRef.current)   topScrollRef.current.scrollLeft   = e.target.scrollLeft; }

  // Extract period definitions from results (same for all rows in a batch).
  // Source priority: BT-4a column `periods_results` (correct {start,end,days} shape) →
  // legacy meta.period_results (random_periods era, same shape) → body additional_periods
  // (NB: body has {start_date,end_date} — normalize for TableRow lookups by .start).
  // Per-period (P1/P2…) expanded columns were a random-periods-era leftover.
  // Periods now live in: aggregate columns (Avg/Worst …) + the 📊 N popup.
  // Keep periodDefs empty so the expanded group headers / sub-columns / chain
  // never render (the rest of the period machinery below is inert when empty).
  const periodDefs = useMemo(() => [], []);
  const hasChainSummary = false;

  // Whether we need the two-row header
  const hasPeriods = periodDefs.length > 0;

  // Client-side sort applied on top of server-side order
  const sortedResults = useMemo(() => {
    let base = results;

    if (periodSort) {
      const { idx, metric, dir } = periodSort;
      const key = metric === 'monthly' ? 'monthly_return' : 'yearly_return';
      base = [...base].sort((a, b) => {
        let va, vb;
        if (idx === 'chain') {
          va = a._meta?.period_chain_summary?.[key] ?? -Infinity;
          vb = b._meta?.period_chain_summary?.[key] ?? -Infinity;
        } else {
          const start = periodDefs[idx]?.start;
          const prA = (a._meta?.period_results || []).find(p => p.start === start);
          const prB = (b._meta?.period_results || []).find(p => p.start === start);
          va = prA?.[key] ?? -Infinity;
          vb = prB?.[key] ?? -Infinity;
        }
        return (va - vb) * dir;
      });
    }

    return base;
  }, [results, periodSort, periodDefs]);

  function togglePeriodSort(idx, metric) {
    setPeriodSort(prev => {
      if (prev && prev.idx === idx && prev.metric === metric) {
        return { idx, metric, dir: prev.dir * -1 };
      }
      return { idx, metric, dir: -1 }; // default: descending
    });
  }

  // Each period → 2 sub-columns (monthly + yearly); chain → 2 sub-columns
  const periodColCount = periodDefs.length * 2 + (hasChainSummary ? 2 : 0);

  // 'data' (тип даних) прибрано з таблиці; у збережених налаштуваннях людини
  // ключ може лишатись увімкненим — не рахуємо його в ширину рядка.
  const visibleCount = Object.entries(visibleColumns).filter(([k, v]) => v && k !== 'data').length;
  // checkbox, #, star, name, updated, status, mock, actions = 8 fixed
  const totalCols = visibleCount + 8 + periodColCount;

  const thFixed = {
    ...th,
    background: '#F1F5F9',
    position: 'sticky',
    top: 0,
    zIndex: 4,
  };

  // rowSpan for regular columns when two-row header is active
  const rs = hasPeriods ? 2 : undefined;

  const allChecked = results.length > 0 && results.every(r => checkedIds.has(r.id));

  return (
    <>
      {/* Loading overlay */}
      {loading && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          padding: '12px 18px',
          background: '#EFF6FF',
          borderBottom: '1px solid #BFDBFE',
          color: '#2563EB',
          fontSize: 13,
          fontWeight: 500,
        }}>
          <span style={{
            display: 'inline-block',
            width: 16, height: 16,
            border: '2px solid #93C5FD',
            borderTopColor: '#2563EB',
            borderRadius: '50%',
            animation: 'analyzerSpin 0.7s linear infinite',
          }} />
          Searching…
        </div>
      )}

      <style>{`
        @keyframes analyzerPulse { 0%,100%{opacity:1} 50%{opacity:0.45} }
        @keyframes analyzerSpin  { to { transform: rotate(360deg); } }
        .analyzer-skeleton { animation: analyzerPulse 1.4s ease-in-out infinite; }
        .analyzer-top-scroll { overflow-x: scroll; overflow-y: hidden; height: 14px; border-bottom: 1px solid #E2E8F0; }
        .analyzer-top-scroll::-webkit-scrollbar { height: 14px; }
        .analyzer-top-scroll::-webkit-scrollbar-track { background: #F1F5F9; }
        .analyzer-top-scroll::-webkit-scrollbar-thumb { background: #94A3B8; border-radius: 7px; border: 3px solid #F1F5F9; }
        .analyzer-top-scroll::-webkit-scrollbar-thumb:hover { background: #64748B; }
        .analyzer-table-scroll { overflow-x: scroll; overflow-y: auto; max-height: 70vh; }
        .analyzer-table-scroll::-webkit-scrollbar { height: 8px; }
        .analyzer-table-scroll::-webkit-scrollbar-track { background: #F1F5F9; }
        .analyzer-table-scroll::-webkit-scrollbar-thumb { background: #94A3B8; border-radius: 4px; }
        .analyzer-table-scroll::-webkit-scrollbar-thumb:hover { background: #64748B; }
      `}</style>

      <div ref={topScrollRef} className="analyzer-top-scroll" onScroll={syncFromTop}>
        <div style={{ width: tableScrollWidth, height: 1 }} />
      </div>

      <div ref={tableScrollRef} className="analyzer-table-scroll" onScroll={syncFromBottom}>
        <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%', fontSize: 12 }}>
          <thead>
            {/* ── Row 1 (always) ── */}
            <tr>
              {/* Sticky: checkbox */}
              <th
                rowSpan={rs}
                style={{ ...thFixed, left: 0, width: 36, textAlign: 'center', padding: '9px 6px', zIndex: 5 }}
                onClick={e => { e.stopPropagation(); onSelectAll(); }}
              >
                <input type="checkbox" readOnly style={{ cursor: 'pointer', accentColor: C.primary }} checked={allChecked} />
              </th>

              {/* Sticky: # */}
              <SortTh col="id" sortCol={sortCol} sortDir={sortDir} onSort={onSort}
                rowSpan={rs} style={{ ...thFixed, left: 36, zIndex: 5, width: 50 }}
              >#</SortTh>

              {/* Star */}
              <th rowSpan={rs} style={{
                ...th, width: 38, textAlign: 'center', padding: '9px 4px', cursor: 'default',
                background: showFavoritesOnly ? '#FEF3C7' : '#F8FAFC',
                color: showFavoritesOnly ? '#D97706' : C.textSm,
              }} title="Favorites filter" onClick={onToggleFavorites}>
                {showFavoritesOnly ? '★' : '☆'}
              </th>

              {/* Name */}
              <SortTh col="name" sortCol={sortCol} sortDir={sortDir} onSort={onSort}
                rowSpan={rs} style={{ minWidth: 200 }}
              >Name</SortTh>
              <SortTh rowSpan={rs} col="updated_at" sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Updated</SortTh>

              {visibleColumns.buy_pct  && <SortTh rowSpan={rs} col="buy_pct"       sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Buy%</SortTh>}
              {visibleColumns.t_buy    && <SortTh rowSpan={rs} col="buy_window"    sortCol={sortCol} sortDir={sortDir} onSort={onSort}>T-Buy</SortTh>}
              {visibleColumns.intraday && <SortTh rowSpan={rs} col="intraday"      sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Intraday</SortTh>}
              {visibleColumns.drop_pct && <SortTh rowSpan={rs} col="sell_pct"      sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Drop%</SortTh>}
              {visibleColumns.t_sell   && <SortTh rowSpan={rs} col="sell_window"   sortCol={sortCol} sortDir={sortDir} onSort={onSort}>T-Sell</SortTh>}
              {visibleColumns.n_cos    && <SortTh rowSpan={rs} col="n_companies"   sortCol={sortCol} sortDir={sortDir} onSort={onSort}>N cos.</SortTh>}
              {visibleColumns.pos_pct  && <SortTh rowSpan={rs} col="position_size" sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Pos%</SortTh>}
              {visibleColumns.capital  && <SortTh rowSpan={rs} col="capital"       sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Capital</SortTh>}
              {visibleColumns.win_rate && <SortTh rowSpan={rs} col="win_rate"      sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Win%</SortTh>}
              {visibleColumns.monthly  && (
                <SortTh rowSpan={rs} col="monthly_return" sortCol={sortCol} sortDir={sortDir} onSort={onSort}>
                  Monthly%
                </SortTh>
              )}
              {visibleColumns.yearly   && <SortTh rowSpan={rs} col="yearly_return" sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Yearly%</SortTh>}
              {visibleColumns.worst_yearly_periods && (
                <SortTh rowSpan={rs} col="worst_yearly_return_periods"
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort}
                  title="Найгірший Yearly% серед усіх періодів">Worst Yr%</SortTh>
              )}
              {visibleColumns.neg_yearly_share_periods && (
                <SortTh rowSpan={rs} col="neg_yearly_share_periods"
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort}
                  title="Частка збиткових періодів (Yearly% < 0). Worst Yr% каже, наскільки глибоко буває погано; ця колонка — як часто">% of worst Yr</SortTh>
              )}
              {visibleColumns.neg_yearly_avg_periods && (
                <SortTh rowSpan={rs} col="neg_yearly_avg_periods"
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort}
                  title="Середній Yearly% лише по збиткових періодах">Average % in worst Yr</SortTh>
              )}
              {visibleColumns.best_yearly_periods && (
                <SortTh rowSpan={rs} col="best_yearly_return_periods"
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort}
                  title="Найкращий Yearly% серед усіх періодів">Best Yr%</SortTh>
              )}
              {visibleColumns.pos_yearly_share_periods && (
                <SortTh rowSpan={rs} col="pos_yearly_share_periods"
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort}
                  title="Частка прибуткових періодів (Yearly% ≥ 0.00). Разом із «% of worst Yr» дає рівно 100%">% of no worst Yr</SortTh>
              )}
              {visibleColumns.pos_yearly_avg_periods && (
                <SortTh rowSpan={rs} col="pos_yearly_avg_periods"
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort}
                  title="Середній Yearly% лише по прибуткових періодах">Average % in no worst Yr</SortTh>
              )}
              {visibleColumns.best_worst_yearly_periods && (
                <SortTh rowSpan={rs} col="best_minus_worst_yearly_periods"
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort}
                  title="Best Yr% − Worst Yr% (розкид по роках; менше = стабільніше). Сортуй за зростанням і шукай першу велику дохідність">Best-worst Yr%</SortTh>
              )}
              {visibleColumns.avg_yearly_periods && (
                <SortTh rowSpan={rs} col="avg_yearly_return_periods"
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort}
                  title="Середній Yearly% по всіх періодах">Avg. Yr%</SortTh>
              )}
              {visibleColumns.avg_deals_periods && (
                <SortTh rowSpan={rs} col="avg_deals_per_year_periods"
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort}
                  title="Скільки угод на рік у середньому по періодах. Угода = рядок журналу, тобто рахуються обидві ноги: покупки + продажі. Усі угоди всіх періодів діляться на сумарну кількість років у цих періодах (днів / 365)">Avg. Deals in Yr</SortTh>
              )}
              {visibleColumns.periods_count && <SortTh rowSpan={rs} col="periods_count" sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Periods</SortTh>}
              {visibleColumns.sharpe       && <SortTh rowSpan={rs} col="sharpe"         sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Sharpe</SortTh>}
              {visibleColumns.maxdd        && <SortTh rowSpan={rs} col="max_dd"         sortCol={sortCol} sortDir={sortDir} onSort={onSort}>MaxDD</SortTh>}
              {visibleColumns.prof_months  && <SortTh rowSpan={rs} col="profitable_months_pct" sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="% місяців з позитивною доходністю (80%+ = стабільна в часі)">Profit.Mo%</SortTh>}
              {visibleColumns.worst_month  && <SortTh rowSpan={rs} col="worst_month"          sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Найгірший місяць (менша втрата = краще)">Worst Mo%</SortTh>}
              {visibleColumns.calmar       && <SortTh rowSpan={rs} col="calmar_ratio"          sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Calmar = Monthly% / MaxDD (вище = краще)">Calmar</SortTh>}
              {visibleColumns.overnight    && <SortTh rowSpan={rs} col="overnight"    sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Overnight</SortTh>}
              {visibleColumns.weekend      && <SortTh rowSpan={rs} col="weekend"       sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Weekend</SortTh>}
              {visibleColumns.max_loss     && <SortTh rowSpan={rs} col="max_loss"      sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Max Loss%</SortTh>}
              {visibleColumns.sell_mode    && <SortTh rowSpan={rs} col="sell_mode"     sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Sell</SortTh>}
              {visibleColumns.stagnation   && <SortTh rowSpan={rs} col="stagnation"    sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Stagnation</SortTh>}
              {visibleColumns.reinvest     && <SortTh rowSpan={rs} col="reinvest"      sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Reinvest</SortTh>}
              {/* Сортування ВСІХ колонок (менеджер 26.08). Воно серверне: колонки,
                  якої немає в _ANALYZER_SORT_MAP, для сервера не існує — саме тому
                  нові стовпці не реагували на клік по шапці. Вимкнений механізм
                  дає NULL і лягає в кінець, а не вдає нульовий поріг. */}
              {visibleColumns.hard_stop    && <SortTh rowSpan={rs} col="hard_stop"    sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Hard Stop</SortTh>}
              {visibleColumns.peak_window  && <SortTh rowSpan={rs} col="peak_window"  sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Peak Win</SortTh>}
              {visibleColumns.buy_cap      && <SortTh rowSpan={rs} col="buy_cap"      sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Buy Cap</SortTh>}
              {visibleColumns.overpriced   && <SortTh rowSpan={rs} col="overpriced"   sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Сортування за % (вікно в барах — друге число клітинки)">Overpriced</SortTh>}
              {visibleColumns.dip_pct      && <SortTh rowSpan={rs} col="dip_pct"      sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Наскільки глибоко ціна нижче своєї середньої, щоб папір став кандидатом">Dip %</SortTh>}
              {visibleColumns.dip_bars     && <SortTh rowSpan={rs} col="dip_bars"     sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Довжина вікна середньої, у барах">Dip bars</SortTh>}
              {visibleColumns.rotation     && <SortTh rowSpan={rs} col="rotation"     sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Сортування за мін. утриманням у барах (множник — друге число клітинки)">Rotation</SortTh>}
              {visibleColumns.opportunity  && <SortTh rowSpan={rs} col="opportunity"  sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Сортування за мін. утриманням у барах">Opport.</SortTh>}
              {visibleColumns.hold_to_cost && <SortTh rowSpan={rs} col="hold_to_cost" sortCol={sortCol} sortDir={sortDir} onSort={onSort}>HoldCost</SortTh>}
              {visibleColumns.htc_cost     && <SortTh rowSpan={rs} col="htc_cost"     sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Hold to Cost відпускає позицію на беззбитковості (комісії купівлі й продажу + спред) плюс ця націнка; — = від ціни купівлі">HTC +%</SortTh>}
              {visibleColumns.swap_defer   && <SortTh rowSpan={rs} col="swap_defer"   sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="[2c] Сигнал продажу виконується лише під покупку">Sell→Buy</SortTh>}
              {visibleColumns.swap_tp_frac && <SortTh rowSpan={rs} col="swap_tp_frac" sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="[2d] Продаж під покупку від цієї частки цілі Take Profit">TP Frac%</SortTh>}
              {visibleColumns.swap_htc     && <SortTh rowSpan={rs} col="swap_htc"     sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="[3] Позицію, яку тримає Hold to Cost, дозволено продати під покупку">HTC→Buy</SortTh>}
              {visibleColumns.no_growth    && <SortTh rowSpan={rs} col="no_growth"    sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Buy% на виході: «утрим.» — продаж, якщо після Sell Window ціна < купівлі + Buy% (no_growth); «вхід» — Buy% лише ініціює купівлю">Buy% exit</SortTh>}
              {visibleColumns.smart_gen    && <SortTh rowSpan={rs} col="smart_gen"    sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Смарт-пошук: у якому поколінні знайдено цю стратегію (— = звичайний перебір)">Smart gen</SortTh>}
              {visibleColumns.tp_pct       && <SortTh rowSpan={rs} col="tp_pct"       sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Поріг фіксації прибутку">Take Profit %</SortTh>}
              {visibleColumns.tp_bars      && <SortTh rowSpan={rs} col="tp_bars"      sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Скільки барів діє тейк-профіт (0 = без обмеження)">Take Profit bars</SortTh>}
              {visibleColumns.tp_sell_immediately && <SortTh rowSpan={rs} col="tp_mode" sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Sell immed.</SortTh>}
              {/* Шари реалізму (блоки 5-10 форми). Усі вимкнені за замовчуванням —
                  рядок без прапорця рахований старими правилами. */}
              {visibleColumns.liquidity    && <SortTh rowSpan={rs} col="liquidity"    sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Фільтр обігу + ліміт заявки часткою денного обігу (сортування за лімітом заявки)">Liquidity</SortTh>}
              {visibleColumns.impact       && <SortTh rowSpan={rs} col="impact"       sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Плата за вплив на ціну">Impact</SortTh>}
              {visibleColumns.margin       && <SortTh rowSpan={rs} col="margin"       sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Маржинальне плече">Leverage</SortTh>}
              {visibleColumns.pit_universe && <SortTh rowSpan={rs} col="pit_universe" sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Капіталізація на момент бару (архівна)">Hist. Cap</SortTh>}
              {visibleColumns.delisting    && <SortTh rowSpan={rs} col="delisting"    sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Делістинги: збиток від виходу з біржі реалізується">Delisting</SortTh>}
              {visibleColumns.reorg        && <SortTh rowSpan={rs} col="reorg"        sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Під тікером почала торгуватись інша компанія: позиція закривається на останньому дні старої">Issuer Chg</SortTh>}
              {visibleColumns.per_bar_universe && <SortTh rowSpan={rs} col="per_bar_universe" sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Належність до всесвіту рахується на кожному барі">Univ./bar</SortTh>}
              {visibleColumns.slicing      && <SortTh rowSpan={rs} col="slicing"      sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Набір позиції частинами (сортування за мін. частиною, $)">Slicing</SortTh>}
              {visibleColumns.partial_sells && <SortTh rowSpan={rs} col="partial_sells" sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Вихід частинами під ліміт денного обігу">Partial Sell</SortTh>}
              {visibleColumns.dynamic_n    && <SortTh rowSpan={rs} col="dynamic_n"    sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Кількість компаній — стартова, а не стеля">Dynamic N</SortTh>}
              {visibleColumns.buy_priority && <SortTh rowSpan={rs} col="buy_priority" sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Кого купувати першим, коли кандидатів більше, ніж вільних місць">Buy Priority</SortTh>}
              {visibleColumns.cap_set      && <SortTh rowSpan={rs} col="cap_set"      sortCol={sortCol} sortDir={sortDir} onSort={onSort} title="Набір класів капіталізації саме цього рядка (вісь перебору)">Cap Set</SortTh>}
              {visibleColumns.regime       && <SortTh rowSpan={rs} col="regime"      sortCol={sortCol} sortDir={sortDir} onSort={onSort} style={{ width: 50 }}>Regime</SortTh>}
              {visibleColumns.tickers      && <SortTh rowSpan={rs} col="tickers"     sortCol={sortCol} sortDir={sortDir} onSort={onSort} style={{ width: 130 }} title="Сортування за режимом вибору (усі / конкретні тікери)">Tickers</SortTh>}
              {visibleColumns.sector       && <SortTh rowSpan={rs} col="sector"      sortCol={sortCol} sortDir={sortDir} onSort={onSort} style={{ width: 100 }}>Sector</SortTh>}
              {visibleColumns.market_cap   && <SortTh rowSpan={rs} col="market_cap"  sortCol={sortCol} sortDir={sortDir} onSort={onSort} style={{ width: 70 }}>Cap</SortTh>}
              {visibleColumns.country      && <SortTh rowSpan={rs} col="country"     sortCol={sortCol} sortDir={sortDir} onSort={onSort} style={{ width: 70 }}>Country</SortTh>}

              {/* Period group headers (colspan=2 each) */}
              {periodDefs.map((period, i) => (
                <th key={`pg${i}`} colSpan={2} style={{
                  ...th,
                  background: '#DCFCE7',
                  textAlign: 'center',
                  borderLeft: i === 0 ? '2px solid #86EFAC' : '1px solid #BBF7D0',
                  borderBottom: '1px solid #86EFAC',
                  padding: '5px 8px',
                }}>
                  <div style={{ fontWeight: 700, color: '#15803D', fontSize: 12 }}>
                    P{i + 1}{' '}
                    <span
                      title={`${period.start} – ${period.end} (${period.days} days)`}
                      style={{
                        cursor: 'help', color: '#16A34A', fontSize: 11,
                        borderBottom: '1px dotted #16A34A',
                      }}
                    >ⓘ</span>
                  </div>
                </th>
              ))}

              {/* Chain group header (colspan=2) */}
              {hasChainSummary && (
                <th colSpan={2} style={{
                  ...th,
                  background: '#EDE9FE',
                  textAlign: 'center',
                  borderLeft: '2px solid #A78BFA',
                  borderBottom: '1px solid #A78BFA',
                  padding: '5px 8px',
                }}>
                  <div style={{ fontWeight: 700, color: '#5B21B6', fontSize: 12 }}>⛓ Chain</div>
                  <div style={{ fontSize: 10, color: '#6B7280' }}>all periods sequential</div>
                </th>
              )}

              <SortTh rowSpan={rs} col="status"     sortCol={sortCol} sortDir={sortDir} onSort={onSort}>Status</SortTh>
              <th
                rowSpan={rs}
                style={{
                  ...th,
                  cursor: 'pointer',
                  background: sortCol === 'mock' ? '#DCFCE7' : '#F0FDF4',
                  color: sortCol === 'mock' ? '#15803D' : C.textSm,
                  fontWeight: sortCol === 'mock' ? 700 : 600,
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  minWidth: 72,
                }}
                title="Sort by mock running status (server-side, all rows)"
                onClick={() => onSort('mock')}
              >
                ▶ Mock {sortCol === 'mock' ? (sortDir > 0 ? '↑' : '↓') : ''}
              </th>
              <th rowSpan={rs} style={th}>Actions</th>
            </tr>

            {/* ── Row 2 — period sub-column headers (only when periods exist) ── */}
            {hasPeriods && (
              <tr>
                {periodDefs.map((period, i) => {
                  const moActive = periodSort?.idx === i && periodSort?.metric === 'monthly';
                  const yrActive = periodSort?.idx === i && periodSort?.metric === 'yearly';
                  return (
                    <React.Fragment key={`ps${i}`}>
                      <PeriodSubTh
                        active={moActive} dir={periodSort?.dir}
                        onClick={() => togglePeriodSort(i, 'monthly')}
                        style={{ borderLeft: i === 0 ? '2px solid #86EFAC' : '1px solid #BBF7D0' }}
                      >
                        Mo%
                      </PeriodSubTh>
                      <PeriodSubTh
                        active={yrActive} dir={periodSort?.dir}
                        onClick={() => togglePeriodSort(i, 'yearly')}
                        style={{ borderRight: '1px solid #BBF7D0' }}
                      >
                        Yr%
                      </PeriodSubTh>
                    </React.Fragment>
                  );
                })}
                {hasChainSummary && (() => {
                  const moActive = periodSort?.idx === 'chain' && periodSort?.metric === 'monthly';
                  const yrActive = periodSort?.idx === 'chain' && periodSort?.metric === 'yearly';
                  return (
                    <>
                      <ChainSubTh active={moActive} dir={periodSort?.dir} onClick={() => togglePeriodSort('chain', 'monthly')}>
                        Mo%
                      </ChainSubTh>
                      <ChainSubTh active={yrActive} dir={periodSort?.dir} onClick={() => togglePeriodSort('chain', 'yearly')}>
                        Yr%
                      </ChainSubTh>
                    </>
                  );
                })()}
              </tr>
            )}
          </thead>

          <tbody>
            {loading && sortedResults.length === 0 && (
              Array.from({ length: 8 }).map((_, i) => (
                <SkeletonRow key={i} cols={totalCols} />
              ))
            )}

            {!loading && sortedResults.length === 0 && (
              <tr>
                <td colSpan={totalCols} style={{
                  textAlign: 'center', padding: 40,
                  color: C.textSm, fontSize: 13,
                }}>
                  No results yet. Fill in the parameters above and click "Save & Search".
                </td>
              </tr>
            )}

            {sortedResults.map((r, idx) => (
              <TableRow
                key={r.id}
                r={r}
                idx={idx}
                pageOffset={pageOffset}
                isChecked={checkedIds.has(r.id)}
                isStarred={starredIds.has(r.id)}
                visibleColumns={visibleColumns}
                sortCol={sortCol}
                periodDefs={periodDefs}
                hasChainSummary={hasChainSummary}
                onCheck={onToggleCheck}
                onStar={onToggleStar}
                onView={onViewDetail}
                onCopy={onCopyToForm}
                onRunMock={onRunMock}
                onRecalc={onRecalcOne}
                onDelete={onDelete}
                onOpenPeriods={onOpenPeriods}
                recalcingId={recalcingId}
                runningBotNames={runningBotNames}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
